// Legacy parity reference seeding the active map; applyDbDownloadLayersBundle refills it from the
// DB-derived map when the server delivers one.
//
// Source of truth for available download layers: key = layer picker id, value = ESRI mapping.
// Nesting needs no per-section handling: NewDowloadStore.collectLayerIds flattens a section to
// its leaf ids and the toggle applies them in one pass.
// Layer names from https://mapserver-new.civilgrid.com/server/rest/services/Tools/ExtractData8/GPServer/Extract%20Data%208
const LEGACY_LAYER_ID_TO_DOWNLOAD_LAYER: { [key: string]: string } = {
  buildings: 'Building Footprints',
  contourLines3ft: 'Contour Lines (full resolution)',
  electricTransmissionStructures: 'Electric Transmission Structures',
  electricTransmissionLines: 'Electric Transmission Lines',
  electricDistributionLines: 'Electric Distribution Lines',
  electricDistributionStructures: 'Electric Distribution Structures',
  electricDistributionServiceLines: 'Electric Distribution Service Lines',
  electricDistributionOther: 'Electric Distribution Other',
  electricDistributionServiceVaults: 'Electric Distribution Vaults',
  gasPoints: 'Gas Points',
  gasDistribution: 'Gas Distribution',
  gasTransmission: 'Gas_Transmission',
  gasService: 'Gas Service',
  // geotechnicalGroundwaterStations: 'Groundwater Stations', // TEMP removed
  environmentalSacramentoProtectedTrees: 'Sacramento Protected Trees',
  landRightsParcels: 'Parcels',
  sanitaryPoints: 'Sanitary Points',
  sanitaryPipes: 'Sanitary Pipes',
  // soils: 'Soil Classification', // TEMP removed
  stormPoints: 'Storm Points',
  stormPipes: 'Storm Pipes',
  waterPoints: 'Water Points',
  waterMain: 'Water Main',
  waterTransmission: 'Water Transmission',
  waterService: 'Water Service',
  recycledWaterPipes: 'Recycled_Pipes',
  recycledWaterPoints: 'Recycled_Points',
  // geotechnicalOilGasWells: 'Wells', // TEMP removed
  landUseCurbData: 'Curb',
  fiberLitBuildings: 'Fiber Lit Buildings',
  cellTowers: 'Cell Towers',
  submarineLandings: 'Submarine Landings',
  fiberRoutes: 'Fiber Routes',
  submarineCables: 'Submarine Routes',
  // this currently requires approval from partner regardless of project specs, but we include it here to populate the layer list
  // it will eventually be less restrictive
  fossilFuelPipelines: 'Fossil Fuel Pipelines',

  // digitized datasets
  digitizedElectricVaults: 'Digitized_Electric_Vaults',
  digitizedElectricStructures: 'Digitized_Electric_Structures',
  digitizedElectricLines: 'Digitized_Electric_Lines',
  digitizedGasPoints: 'Digitized_Gas_Points',
  digitizedGasLines: 'Digitized_Gas_Pipes',
  digitizedSewerPoints: 'Digitized_Sewer_Points',
  digitizedSewerLines: 'Digitized_Sewer_Lines',
  digitizedWaterPoints: 'Digitized_Water_Points',
  digitizedWaterLines: 'Digitized_Water_Lines',
  digitizedRecycledWaterLines: 'Digitized_Recycled_Water_Lines',
  digitizedRecycledWaterPoints: 'Digitized_Recycled_Water_Points',
  digitizedStormPoints: 'Digitized_Storm_Points',
  digitizedStormLines: 'Digitized_Storm_Lines',
  digitizedTelcoLines: 'Digitized_Telco_Lines',
  digitizedTelcoPoints: 'Digitized_Telco_Points',
  digitizedFuelPoints: 'Digitized_Fuel_Points',
  digitizedFuelLines: 'Digitized_Fuel_Lines',
  digitizedTrafficSignals: 'Digitized_Signal_Points',
  digitizedTrafficSignalLines: 'Digitized_Signal_Lines',
  digitizedOtherPoints: 'Digitized_Other_Points',
  digitizedOtherLines: 'Digitized_Other_Lines',
  digitizedEasements: 'Digitized_Easements',

  // SCE (Special Dataset)
  sceElectricPoints: 'SCE_Electric_point',
  sceStormPoints: 'SCE_Storm_point',
  scePropertyPoints: 'SCE_Property_point',
  sceWaterPoints: 'SCE_Water_point',
  sceLandscapePoints: 'SCE_Landscape_point',
  sceOtherUtilityPoints: 'SCE_Other_Utility_point',
  sceOtherPoints: 'SCE_Other_point',
  sceStreetPoints: 'SCE_Street_point',
  sceSewerPoints: 'SCE_Sewer_point',
  sceSurveyPoints: 'SCE_Survey_point',
  sceGasAndOilPoints: 'SCE_Gas_Oil_point',
  sceElectricOtherLines: 'SCE_Electric_Other_polylines',
  sceStormLines: 'SCE_Storm_polylines',
  scePropertyLines: 'SCE_Property_polylines',
  sceWaterLines: 'SCE_Water_polylines',
  sceLandscapeLines: 'SCE_Landscape_polylines',
  sceOtherUtilityLines: 'SCE_Other_Utility_polylines',
  sceOtherLines: 'SCE_Other_polylines',
  sceStreetLines: 'SCE_Street_polylines',
  sceSewerLines: 'SCE_Sewer_polylines',
  sceSurveyLines: 'SCE_Survey_polylines',
  sceGasAndOilLines: 'SCE_Gas_Oil_polylines',
  sceTelcoLines: 'SCE_Telco_polylines',
  sceAnnotationPoints: 'SCE_Annotation_point',
  sceTelcoPoints: 'SCE_Telco_point',
  sceElectricInstallLines: 'SCE_Electric_Install_polylines',
  sceElectricRemoveLines: 'SCE_Electric_Remove_polylines',
  sceElectricExistingLines: 'SCE_Electric_Existing_polylines',
  sceDimensionPoints: 'SCE_Dimension_point',
  sceDimensionLines: 'SCE_Dimension_polylines',
};

// Active singleton: keeps its object identity while contents are swapped in place, so the
// call-time consumers (download picker filter, Extract Data 8 id -> GPServer-name lookup,
// downloads-page reverse lookup) keep working without edits.
export const layerIdToDownloadLayer: { [key: string]: string } = { ...LEGACY_LAYER_ID_TO_DOWNLOAD_LAYER };
export const layerIdToDownloadLayerOverrides: { [key: string]: string } = {
  landRightsParcels: 'Parcels_Production',
};

function fill(next: { [key: string]: string }): void {
  for (const key of Object.keys(layerIdToDownloadLayer)) delete layerIdToDownloadLayer[key];
  for (const [key, value] of Object.entries(next)) layerIdToDownloadLayer[key] = value;
}

/**
 * Refill the active download map from the server's DB-derived map.
 * GPServer name strings are carried verbatim — they ARE the Extract Data 8 request payload.
 */
export function applyDbDownloadLayersBundle(map: { [key: string]: string }): void {
  fill({ ...map });
}

/**
 * Restore the active download map to the legacy source. Must run on every org switch in the
 * no-delivery branch so a previous org's DB map can't leak through.
 */
export function resetDownloadLayersToLegacy(): void {
  fill({ ...LEGACY_LAYER_ID_TO_DOWNLOAD_LAYER });
}
